import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import CategoryTable from '../../components/Table/CategoryTable/CategoryTable';
import Header from '../../components/Header/Header';
import Sidebar from '../../components/Sidebar/Sidebar';
import { useDispatch } from 'react-redux';
import CompaniesModal from './CompaniesModal';
import CategoryModal from '../../components/Shared/Modals/CategoryModal';
import { addCompany, deleteCompany, updateCompany, getAllCompanies } from '../../services/operations/companies';
import Fuse from 'fuse.js';

const environmentTabs = [
    { label: 'DEV', value: 'dev' },
    { label: 'PROD', value: 'prod' }
];

const AllCompanies = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const { AllCompanies } = useSelector((state) => state.companies);
    const [modalOpen, setModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [companyToDelete, setCompanyToDelete] = useState(null);
    const [isEdit, setIsEdit] = useState(false);
    const [FilteredCompanaies, setFilteredCompanies] = useState(AllCompanies || []);
    const [currentEnv, setCurrentEnv] = useState(() => {
        const isDev = localStorage.getItem('isDev') === 'true';
        return isDev ? 'dev' : 'prod';
    });

    const {
        handleSubmit,
        control,
        setValue,
        formState: { errors, isSubmitted },
        reset,
    } = useForm({
        defaultValues: {},
    });

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(getAllCompanies());
    }, [dispatch]);

    useEffect(() => {
        setFilteredCompanies(AllCompanies || []);
    }, [AllCompanies]);

    const fuse = new Fuse(AllCompanies, {
        includeScore: true,
        threshold: 0.2,
        keys: ['name', 'type']
    });

    const handleAddCompany = (e) => {
        e.stopPropagation();
        setIsEdit(false);
        reset({
            id: '',
            name: '',
            logo: '',
            type: ''
        });
        setModalOpen(true);
    };

    const onSubmit = (data) => {
        const { id, name, logo, type } = data;
        if (isEdit) {
            dispatch(updateCompany({ id, name, logo, type }, setModalOpen));
            return;
        } else {
            dispatch(addCompany({ name, logo, type }, setModalOpen, reset));
        }
    };

    const handleCompaniesEdit = (e, companiesEdit) => {
        e.stopPropagation();
        const { id, company_name, company_logo, company_type } = companiesEdit;
        console.log(companiesEdit);
        setValue('id', id);
        setValue('name', company_name);
        setValue('logo', company_logo);
        setValue('type', company_type);
        setIsEdit(true);
        setModalOpen(true);
    }

    const handleCompaniesDelete = (e, companiesDelete) => {
        e.stopPropagation();
        setCompanyToDelete(companiesDelete);
        setDeleteModalOpen(true);
    }

    const confirmDelete = () => {
        if (companyToDelete) {
            dispatch(deleteCompany(companyToDelete.id));
            setDeleteModalOpen(false);
            setCompanyToDelete(null);
        }
    }

    const [searchOpen, setSearchOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const handleSearch = (e) => {
        const term = e.target.value;
        setSearchTerm(term);

        if (term.trim() === '') {
            setFilteredCompanies(AllCompanies);
            return;
        }

        const results = fuse.search(term);
        const filtered = results.map((result) => result.item);

        setFilteredCompanies(filtered);
    };

    const toggleSearch = () => {
        setSearchOpen(!searchOpen);
        if (searchOpen) {
            setSearchTerm('');
            setFilteredCompanies(AllCompanies);
        }
    };

    const handleEnvChange = (env) => {
        setCurrentEnv(env);
        localStorage.setItem('isDev', env === 'dev');
        dispatch(getAllCompanies());
    };

    return (
        <div className='flex dark:bg-dark font-primary h-screen overflow-hidden bg-[#fafafa]'>
            <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
            <div className='relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden'>
                <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
                <main>
                    <div className='px-4 mb-8 sm:px-6 lg:px-8 w-full max-w-9xl mx-auto'>
                        <div className='flex justify-between items-center px-6 py-4'>
                            <h2 className='text-2xl font-bold text-[#212121] dark:text-zinc-200'>
                                All Companies
                            </h2>
                        </div>

                        {/* Environment Tabs */}
                        <div className='flex justify-center my-6 bg-zinc-900 rounded-full text-sm w-5/12 mx-auto'>
                            <TabSwitcher
                                tabs={environmentTabs}
                                activeTab={currentEnv}
                                onChange={handleEnvChange}
                            />
                        </div>

                        <p className='text-center text-8xl font-bold mt-4 text-zinc-700'>{currentEnv.toUpperCase()}</p>

                        <div className='flex justify-between items-center px-6 py-4'>
                            <button
                                onClick={handleAddCompany}
                                className='bg-[#FFD3B5] dark:bg-zinc-800 text-[#212121] dark:text-zinc-200 font-semibold py-2 px-4 rounded-lg'>
                                Add Company
                            </button>
                            <button
                                onClick={toggleSearch}
                                className='bg-[#FFD3B5] dark:bg-zinc-800 text-[#212121] dark:text-zinc-200 font-semibold py-2 px-4 rounded-lg w-44'
                            >
                                {searchOpen ? 'Close Search' : 'Search'}
                            </button>
                        </div>
                        {searchOpen && (
                            <div className='flex justify-center items-center mb-4'>
                                <input
                                    autoFocus
                                    type='text'
                                    value={searchTerm}
                                    onChange={handleSearch}
                                    placeholder='Search companies by name or type...'
                                    className='w-full max-w-2xl px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500'
                                />
                            </div>
                        )}
                        <div className='flex gap-y-2 flex-col justify-start'>
                            {FilteredCompanaies?.length > 0 ? (
                                <div className='flex flex-col p-4 '>
                                    <CategoryTable
                                        page='companies'
                                        setIsEdit={setIsEdit}
                                        topics={FilteredCompanaies}
                                        setModalOpen={setModalOpen}
                                        modalOpen={modalOpen}
                                        handleEdit={handleCompaniesEdit}
                                        handleDelete={handleCompaniesDelete}
                                    />
                                </div>
                            ) : (
                                <div className='flex justify-center items-center text-4xl dark:text-zinc-300 mt-10'>
                                    No Companies to show
                                </div>
                            )}
                        </div>
                        <CompaniesModal
                            title='Add Company'
                            setModalOpen={setModalOpen}
                            modalOpen={modalOpen}>
                            <form className='mt-6' onSubmit={handleSubmit(onSubmit)}>
                                <div className='flex flex-col flex-wrap -mx-3 mb-4'>
                                    <span className='px-3 text-lg font-semibold text-light_30 mb-2 dark:text-zinc-300'>
                                        Company Name
                                    </span>
                                    <div className='w-full px-3'>
                                        <Controller
                                            name='name'
                                            control={control}
                                            render={({ field }) => (
                                                <input
                                                    id='name'
                                                    type='text'
                                                    name='name'
                                                    className='form-input w-full text-gray-300'
                                                    placeholder='Company Name'
                                                    {...field}
                                                />
                                            )}
                                            rules={{
                                                required: 'Company name is required',
                                            }}
                                        />
                                        {isSubmitted && errors.name && (
                                            <p className='text-red-600 text-sm italic'>
                                                {errors.name.message}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className='flex flex-col flex-wrap -mx-3 mb-4'>
                                    <span className='px-3 text-lg font-semibold text-light_30 mb-2 dark:text-zinc-300'>
                                        Logo
                                    </span>
                                    <div className='w-full px-3'>
                                        <Controller
                                            name='logo'
                                            control={control}
                                            render={({ field }) => (
                                                <input
                                                    id='logo'
                                                    type='text'
                                                    name='logo'
                                                    className='form-input w-full text-gray-300'
                                                    placeholder='Logo URL'
                                                    {...field}
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                                <div className='flex flex-col flex-wrap -mx-3 mb-4'>
                                    <span className='px-3 text-lg font-semibold text-light_30 mb-2 dark:text-zinc-300'>
                                        Type
                                    </span>
                                    <div className='w-full px-3'>
                                        <Controller
                                            name='type'
                                            defaultValue='Product Based'
                                            control={control}
                                            render={({ field }) => (
                                                <select {...field} className='bg-zinc-900 rounded-md w-full'>
                                                    <option value='Product Based'>Product Based</option>
                                                    <option value='Service Based'>Service Based</option>
                                                    <option value='Startup'>Startup</option>
                                                    <option value='Others'>Others</option>
                                                </select>
                                            )}
                                        />
                                    </div>
                                </div>
                                <div className='flex flex-wrap -mx-3 mt-6'>
                                    <div className='w-full px-3'>
                                        <button type='submit' className='btn-brand-2 w-full'>
                                            Submit
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </CompaniesModal>
                    </div>
                </main>
            </div>
            <CategoryModal
                title="Delete Company"
                modalOpen={deleteModalOpen}
                setModalOpen={setDeleteModalOpen}
            >
                <div className='flex flex-col gap-y-4 items-center justify-center'>
                    <span className='text-4xl'>Are You Sure?</span>
                    <p className='text-center text-gray-600 dark:text-gray-400 mb-2'>
                        This will permanently delete the company "{companyToDelete?.company_name}". This action cannot be undone.
                    </p>
                    <button
                        className='btn-brand-2 mt-4'
                        onClick={confirmDelete}
                    >
                        Yes, Delete
                    </button>
                </div>
            </CategoryModal>
        </div>
    );
};

export default AllCompanies;

const TabSwitcher = ({ tabs, activeTab, onChange, size = "md" }) => {
  const sizeClasses = {
    sm: "text-sm py-1.5",
    md: "text-base py-2",
    lg: "text-lg py-2.5"
  };

  return (
    <div className="flex justify-center bg-zinc-900 rounded-full mx-auto w-full">
      {tabs.map((tab) => (
        <button
          key={tab.value}
          onClick={() => onChange(tab.value)}
          className={`
            relative w-full px-4 rounded-full  transition-colors duration-200
            ${sizeClasses[size]}
            ${activeTab === tab.value ? 'text-white' : 'text-zinc-500 hover:text-zinc-400'}
          `}
        >
          {activeTab === tab.value && (
            <div className="absolute inset-0 w-full border-2 border-zinc-700 rounded-full" />
          )}
          <span className="relative z-10 w-full">{tab.label}</span>
        </button>
      ))}
    </div>
  );
};

